/**
 * navConfig.ts — the single source of truth for primary navigation.
 *
 * Every shell (classic RootLayout, the V3 preview shell, the Data 2
 * preview shell) derives its sidebar from NAV_ITEMS. Preview shells hide
 * the `preview` entries and render items they don't redesign as classic
 * link-outs — so a nav item added here shows up everywhere, and the
 * preview sidebars can never silently drop Forecast/Alerts again.
 *
 * Icons are kebab-case lucide-react names (resolved via navIcon) so the
 * config stays serialisable and the datav3 shell's string-based <Ic>
 * can consume it directly.
 */

import * as Lucide from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type NavSection = "workspace" | "library";

export type NavItem = {
  path: string;
  /** i18n key OR a literal label (literal wins). */
  labelKey?: "nav.today";
  label?: string;
  /** Literal fallback for shells that render without the i18n hook. */
  fallbackLabel?: string;
  /** kebab-case lucide-react icon name ("trending-up" → TrendingUp). */
  icon: string;
  section: NavSection;
  /** Only render once the workspace has at least one model. */
  requiresModel?: boolean;
  /** Show a small dot when this side has unread activity. */
  showsActivityDot?: boolean;
  /** Design-preview entries — hidden inside the preview shells themselves. */
  preview?: boolean;
};

export const NAV_ITEMS: NavItem[] = [
  { path: "/home", labelKey: "nav.today", fallbackLabel: "Today", icon: "home", section: "workspace" },
  { path: "/sales-overview", label: "Sales from a file", icon: "chart-no-axes-combined", section: "workspace" },
  {
    // Temporary comparison entry for the redesigned dashboard.
    path: "/today2",
    label: "Today 2 (preview)",
    icon: "sparkles",
    section: "workspace",
    preview: true,
  },
  {
    // V3 "Ledger" Today page inside the classic shell, for comparison.
    // The full V3 shell experience lives at /v3/home.
    path: "/today3",
    label: "Today 3 (preview)",
    icon: "sparkles",
    section: "workspace",
    preview: true,
  },
  {
    // Redesigned Data tab ("Meridian Design System" port). Opens in its
    // own full shell at the top-level /data2 route.
    path: "/data2",
    label: "Data 2 (preview)",
    icon: "sparkles",
    section: "workspace",
    preview: true,
  },
  {
    // Ask a question — the always-reachable AI entry point.
    path: "/ask-meridian",
    label: "Ask a question",
    icon: "message-square",
    section: "workspace",
    requiresModel: true,
  },
  {
    // Forecast — per-product card surface (FORECAST_METHOD_ROUTER Phase 2).
    // One legible card per series: method, reliability, what to do.
    path: "/forecast",
    label: "Forecast",
    icon: "trending-up",
    section: "workspace",
    requiresModel: true,
  },
  {
    // Forecast alerts — manage standing drift watches (see, mute, delete).
    // Created from the /forecast Alert drawer; fire into the bell.
    path: "/alerts",
    label: "Alerts",
    icon: "bell-ring",
    section: "workspace",
    requiresModel: true,
  },
  {
    // Data owns upload + datasets + models. Ungated (also the upload
    // surface). Activity dot tracks data activity (no review queue is wired to it yet).
    path: "/data",
    label: "Data",
    icon: "database",
    section: "workspace",
    showsActivityDot: true,
  },
  {
    // Hierarchies — workspace-scoped reusable structures (dimensions /
    // master data style parent-child lists) shared across multiple models.
    path: "/hierarchies",
    label: "Hierarchies",
    icon: "network",
    section: "workspace",
  },
  {
    // Mappings — source-to-canonical relationship sets, lineage and health.
    path: "/mappings",
    label: "Mappings",
    icon: "git-branch",
    section: "workspace",
  },
  {
    // Model Discovery — scan uploaded workbooks, propose dimensions and
    // hierarchies (flag-gated: API_MODEL_DISCOVERY_ENABLED).
    path: "/model-discovery",
    label: "Discovery",
    icon: "scan-search",
    section: "workspace",
  },
  {
    path: "/models",
    label: "Meridians",
    icon: "box",
    section: "workspace",
  },
  {
    // Forms — workspace-scoped input surfaces (free questionnaires +
    // hierarchy-backed data-entry grids).
    path: "/forms",
    label: "Forms",
    icon: "file-text",
    section: "workspace",
  },
  {
    // Company — the annual report as a living context profile
    // (specs/COMPANY_CONTEXT.md). Ungated: uploading the report is a
    // day-one act, often BEFORE any dataset exists.
    path: "/company",
    label: "Company",
    icon: "building-2",
    section: "workspace",
  },
  {
    path: "/insights",
    label: "Decisions",
    icon: "lightbulb",
    section: "library",
    requiresModel: true,
  },
];

/** kebab-case ("git-branch") → lucide-react component ("GitBranch"). */
const ICON_CACHE = new Map<string, LucideIcon>();

export function navIcon(name: string): LucideIcon {
  const cached = ICON_CACHE.get(name);
  if (cached) return cached;
  const pascal = name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
  const lib = Lucide as unknown as Record<string, LucideIcon>;
  const icon = lib[pascal] ?? lib.HelpCircle;
  ICON_CACHE.set(name, icon);
  return icon;
}
